import React, { useState, useMemo } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { clsx } from 'clsx';

interface CreatableComboboxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  error?: string;
  dataTestId?: string;
  disabled?: boolean;
  hideLabel?: boolean;
}

export const CreatableCombobox: React.FC<CreatableComboboxProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  dataTestId,
  disabled = false,
  hideLabel = false,
}) => {
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    const uniqueOptions = Array.from(new Set(options)).filter(Boolean);
    if (query === '') {
      return uniqueOptions;
    }
    return uniqueOptions.filter((option) =>
      option.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, options]);

  // If the query is not in the options, allow creating it
  const showCreateOption = query.length > 0 && !filteredOptions.some(opt => opt.toLowerCase() === query.toLowerCase());

  // Ensure value is safe string for display
  const displayValue = value || '';

  return (
    <div className="w-full">
      {!hideLabel && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <Combobox 
        value={value} 
        onChange={(val) => {
          if (val) onChange(val);
          setQuery('');
        }} 
        onClose={() => setQuery('')}
        disabled={disabled}
      >
        <div className={clsx("relative", !hideLabel && "mt-1")}>
          <div className={clsx(
            "relative w-full cursor-default overflow-hidden rounded-md bg-white text-left border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 sm:text-sm",
            disabled && "bg-gray-100 cursor-not-allowed opacity-60"
          )}>
            <ComboboxInput
              className={clsx(
                "w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0",
                disabled && "bg-gray-100 cursor-not-allowed"
              )}
              displayValue={() => displayValue}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={disabled ? undefined : placeholder}
              data-test-id={dataTestId}
              disabled={disabled}
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={disabled}>
              <ChevronsUpDown
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          {!disabled && (
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredOptions.length === 0 && !showCreateOption && (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Nothing found.
                </div>
              )}
              
              {showCreateOption && (
                 <ComboboxOption
                  value={query}
                  className={({ focus }) =>
                    clsx(
                      'relative cursor-default select-none py-2 pl-10 pr-4',
                      focus ? 'bg-primary-600 text-white' : 'text-gray-900'
                    )
                  }
                >
                  {({ focus }) => (
                    <>
                      <span className={`block truncate ${focus ? 'font-medium' : 'font-normal'}`}>
                        Create "{query}"
                      </span>
                    </>
                  )}
                </ComboboxOption>
              )}

              {filteredOptions.map((option) => (
                <ComboboxOption
                  key={option}
                  value={option}
                  className={({ focus }) =>
                    clsx(
                      'relative cursor-default select-none py-2 pl-10 pr-4',
                      focus ? 'bg-primary-600 text-white' : 'text-gray-900'
                    )
                  }
                >
                  {({ selected, focus }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {option}
                      </span>
                      {selected ? (
                        <span
                          className={clsx(
                            'absolute inset-y-0 left-0 flex items-center pl-3',
                            focus ? 'text-white' : 'text-primary-600'
                          )}
                        >
                          <Check className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};
